import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../services/supabase';
import { useVolunteerCategories } from '../hooks/useVolunteerCategories';

const Page = styled.div`
  background: var(--background);
  padding: 1.5rem;
  min-height: 100%;
`;

const Card = styled.div`
  background: var(--surface);
  border-radius: 14px;
  padding: 1.5rem;
  border: 1px solid var(--outline);
  box-shadow: 0 12px 24px rgba(17, 24, 39, 0.06);
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
  gap: 1rem;
`;

const TableCard = styled.div`
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 14px;
  box-shadow: 0 12px 24px rgba(17, 24, 39, 0.06);
  overflow: hidden;
  margin-top: 1rem;
`;

const TableWrap = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  th,
  td {
    padding: 0.75rem 1rem;
    border-bottom: 1px solid var(--outline);
    text-align: left;
    font-size: 0.9rem;
    color: var(--onSurfaceVariant);
  }
  th {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--onSurfaceVariant);
    background: var(--surfaceVariant);
  }
  @media (max-width: 768px) {
    th,
    td {
      padding: 0.5rem 0.75rem;
      font-size: 0.85rem;
    }
  }
`;

const Input = styled.input`
  padding: 0.55rem 0.7rem;
  border: 1px solid var(--outline);
  border-radius: 10px;
  width: 100%;
  background: var(--surface);
  color: var(--onSurface);
`;

const Button = styled.button<{ $variant?: 'primary' | 'danger' | 'ghost' }>`
  padding: 0.5rem 0.85rem;
  border-radius: 10px;
  border: none;
  cursor: pointer;
  font-weight: 600;
  background: ${({ $variant }) => {
    if ($variant === 'danger') return 'var(--error)';
    if ($variant === 'ghost') return 'transparent';
    return 'var(--primary)';
  }};
  color: ${({ $variant }) => ($variant === 'ghost' ? 'var(--onSurface)' : 'var(--onPrimary)')};
`;

const InlineRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 120px 120px 120px;
  gap: 0.75rem;
  align-items: center;
`;

const HelperText = styled.p`
  color: var(--onSurfaceVariant);
  margin: 0.5rem 0 0;
  font-size: 0.9rem;
`;

const ErrorText = styled.p`
  color: var(--error);
`;

type CategoryDraft = {
  key: string;
  label: string;
  sort_order: number;
  is_active: boolean;
};

export const VolunteerCategoriesPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useVolunteerCategories();
  const [drafts, setDrafts] = useState<Record<string, CategoryDraft>>({});
  const [newCategory, setNewCategory] = useState<CategoryDraft>({
    key: '',
    label: '',
    sort_order: 0,
    is_active: true,
  });

  useEffect(() => {
    if (!data) return;
    const nextDrafts: Record<string, CategoryDraft> = {};
    data.forEach((category) => {
      nextDrafts[category.id] = {
        key: category.key,
        label: category.label,
        sort_order: category.sort_order,
        is_active: category.is_active,
      };
    });
    setDrafts(nextDrafts);
  }, [data]);

  const sortedCategories = useMemo(() => {
    return [...(data ?? [])].sort((a, b) => {
      if (a.sort_order === b.sort_order) {
        return a.label.localeCompare(b.label);
      }
      return a.sort_order - b.sort_order;
    });
  }, [data]);

  const createCategory = useMutation({
    mutationFn: async () => {
      const key = newCategory.key.trim();
      const label = newCategory.label.trim();
      if (!key || !label) throw new Error('Key and label are required.');
      const { error: insertError } = await supabase.from('volunteer_categories').insert({
        key,
        label,
        sort_order: Number(newCategory.sort_order) || 0,
        is_active: newCategory.is_active,
      });
      if (insertError) throw insertError;
    },
    onSuccess: () => {
      setNewCategory({ key: '', label: '', sort_order: 0, is_active: true });
      queryClient.invalidateQueries({ queryKey: ['volunteerCategories'] });
    },
  });

  const updateCategory = useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: CategoryDraft }) => {
      const trimmedKey = payload.key.trim();
      const trimmedLabel = payload.label.trim();
      if (!trimmedKey || !trimmedLabel) throw new Error('Key and label are required.');
      const { error: updateError } = await supabase
        .from('volunteer_categories')
        .update({
          key: trimmedKey,
          label: trimmedLabel,
          sort_order: Number(payload.sort_order) || 0,
          is_active: payload.is_active,
        })
        .eq('id', id);
      if (updateError) throw updateError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['volunteerCategories'] });
    },
  });

  const deleteCategory = useMutation({
    mutationFn: async (id: string) => {
      const { error: deleteError } = await supabase.from('volunteer_categories').delete().eq('id', id);
      if (deleteError) throw deleteError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['volunteerCategories'] });
    },
  });

  if (isLoading) return <p>Loading categories…</p>;
  if (error) return <ErrorText>Error loading categories.</ErrorText>;

  return (
    <Page>
      <Card>
        <HeaderRow>
          <div>
            <h1>Volunteer Categories</h1>
            <HelperText>
              Keys are stored on volunteer opportunities. Renaming a key will affect existing
              opportunities.
            </HelperText>
          </div>
        </HeaderRow>

        <h3>Add Category</h3>
        <InlineRow style={{ marginBottom: '1rem' }}>
        <Input
          placeholder="Key (e.g. hospitality)"
          value={newCategory.key}
          onChange={(event) => setNewCategory({ ...newCategory, key: event.target.value })}
        />
        <Input
          placeholder="Label (e.g. Hospitality)"
          value={newCategory.label}
          onChange={(event) => setNewCategory({ ...newCategory, label: event.target.value })}
        />
        <Input
          type="number"
          placeholder="Sort"
          value={newCategory.sort_order}
          onChange={(event) =>
            setNewCategory({ ...newCategory, sort_order: Number(event.target.value) || 0 })
          }
        />
        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <input
            type="checkbox"
            checked={newCategory.is_active}
            onChange={(event) => setNewCategory({ ...newCategory, is_active: event.target.checked })}
          />
          Active
        </label>
        <Button type="button" onClick={() => createCategory.mutate()} disabled={createCategory.isPending}>
          {createCategory.isPending ? 'Saving…' : 'Add'}
        </Button>
      </InlineRow>
        {createCategory.isError && <ErrorText>{(createCategory.error as Error).message}</ErrorText>}

        <TableCard>
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <th>Key</th>
                <th>Label</th>
                <th>Sort</th>
                <th>Active</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedCategories.map((category) => {
                const draft = drafts[category.id];
                if (!draft) return null;
                return (
                  <tr key={category.id}>
                    <td>
                      <Input
                        value={draft.key}
                        onChange={(event) =>
                          setDrafts({
                            ...drafts,
                            [category.id]: { ...draft, key: event.target.value },
                          })
                        }
                      />
                    </td>
                    <td>
                      <Input
                        value={draft.label}
                        onChange={(event) =>
                          setDrafts({
                            ...drafts,
                            [category.id]: { ...draft, label: event.target.value },
                          })
                        }
                      />
                    </td>
                    <td>
                      <Input
                        type="number"
                        value={draft.sort_order}
                        onChange={(event) =>
                          setDrafts({
                            ...drafts,
                            [category.id]: {
                              ...draft,
                              sort_order: Number(event.target.value) || 0,
                            },
                          })
                        }
                      />
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        checked={draft.is_active}
                        onChange={(event) =>
                          setDrafts({
                            ...drafts,
                            [category.id]: { ...draft, is_active: event.target.checked },
                          })
                        }
                      />
                    </td>
                    <td style={{ display: 'flex', gap: 8 }}>
                      <Button
                        type="button"
                        onClick={() => updateCategory.mutate({ id: category.id, payload: draft })}
                        disabled={updateCategory.isPending}
                      >
                        Save
                      </Button>
                      <Button
                        type="button"
                        $variant="danger"
                        onClick={() => {
                          if (window.confirm('Delete this category?')) {
                            deleteCategory.mutate(category.id);
                          }
                        }}
                        disabled={deleteCategory.isPending}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            </Table>
          </TableWrap>
        </TableCard>
        {updateCategory.isError && <ErrorText>{(updateCategory.error as Error).message}</ErrorText>}
        {deleteCategory.isError && <ErrorText>{(deleteCategory.error as Error).message}</ErrorText>}
      </Card>
    </Page>
  );
};

export default VolunteerCategoriesPage;
